import { Rectangle } from 'pixi.js';
import { getTotalBounds } from '../textMetricsHelpers';
import type { RgbaString } from '../../../../types';
import { Config } from '../../../../video-data/config';
import { int2rgba, rgbArray2String, string2rgbaArray } from '../../../../libs/videoData';
import type { TextDynamicBoxBaseRendererSettings, Padding } from './TextDynamicBoxBaseRenderer';
import { TextDynamicBoxBaseRenderer } from './TextDynamicBoxBaseRenderer';

import type { TextDynamicGraphicAnimationRenderer } from './TextDynamicGraphicAnimationRenderer';

export type BorderBounds = {
    x: number;
    y: number;
    width: number;
    height: number;
    start0: number;
    end0: number;
    start1: number;
    end1: number;
    points: [x: number, y: number][];
    segmentLengths: number[];
    totalSegmentLength: number;
};

export interface TextDynamicBoxBorderRendererSettings extends TextDynamicBoxBaseRendererSettings {
    type: typeof Config.BOX_BORDER_GRAPHIC;
    start0: number;
    end0: number;
    start1: number;
    end1: number;

    horizontalPadding: number;
    verticalPadding: number;
    borderThickness: number;
    borderColor: number;
    borderAlpha: number;
}

type AnimatableBoxBorderProperties =
    (typeof TextDynamicBoxBorderRenderer.animatableProperties)[number];
export enum AnimatableGeometricBoxBorderProperties {
    borderThickness = 'borderThickness',
    horizontalPadding = 'horizontalPadding',
    verticalPadding = 'verticalPadding',
}
export enum AnimatableNonGeometricBoxBorderProperties {
    start0 = 'start0',
    end0 = 'end0',
    start1 = 'start1',
    end1 = 'end1',
}

/**
 * Renders a border that can be animated.
 */
export class TextDynamicBoxBorderRenderer extends TextDynamicBoxBaseRenderer {
    override readonly type = Config.BOX_BORDER_GRAPHIC;

    start0 = 0.25;
    end0 = 0.75;
    start1 = 0.75;
    end1 = 0.25;
    horizontalPadding = 20;
    verticalPadding = 20;
    borderThickness = 5;
    borderColor = 0xffffff;
    borderAlpha = 0.5;

    override animationConfigurations: TextDynamicGraphicAnimationRenderer<AnimatableBoxBorderProperties>[] =
        [];

    override apply(settings: TextDynamicBoxBorderRendererSettings) {
        super.apply(settings);
        this.start0 = settings.start0;
        this.end0 = settings.end0;
        this.start1 = settings.start1;
        this.end1 = settings.end1;
        this.horizontalPadding = settings.horizontalPadding;
        this.verticalPadding = settings.verticalPadding;
        this.borderThickness = settings.borderThickness;
        this.borderColor = settings.borderColor;
        this.borderAlpha = settings.borderAlpha;
    }

    protected override _getPadding(): Padding {
        const horizontalPadding = this.horizontalPadding + this.borderThickness / 2;
        const verticalPadding = this.verticalPadding + this.borderThickness / 2;
        return {
            top: verticalPadding,
            right: horizontalPadding,
            bottom: verticalPadding,
            left: horizontalPadding,
        };
    }

    protected override _getLineWidth(): number {
        return this.borderThickness;
    }

    _getBounds() {
        const boundRectangles = this._levelBounds.map(
            (bound) => new Rectangle(bound.x, bound.y, bound.width, bound.height),
        );
        return getTotalBounds(boundRectangles);
    }

    static get animatableProperties() {
        return [
            ...TextDynamicBoxBaseRenderer.baseAnimatableProperties,
            ...Object.values(AnimatableNonGeometricBoxBorderProperties),
            ...Object.values(AnimatableGeometricBoxBorderProperties),
        ];
    }

    static get animatableGeometricProperties() {
        return [
            ...TextDynamicBoxBaseRenderer.baseAnimatableGeometricProperties,
            ...Object.values(AnimatableGeometricBoxBorderProperties),
        ];
    }

    override draw(
        context: CanvasRenderingContext2D,
        inclusionFilterByWordIndex: ((ind: number) => boolean) | undefined,
    ) {
        this._levelBounds.forEach((_, i) => {
            if (inclusionFilterByWordIndex && !inclusionFilterByWordIndex(i)) {
                return;
            }
            const [bounds, fill] = this._applyAnimationToIndex(i);
            drawBoxBorder(context, bounds, fill, this.borderThickness);
        });
    }

    /**
     * Apply the clipping path for the element at the given index. For example, if this box is for the line level, and
     * there are 3 lines, then index 1 will apply the clipping path for the 2nd line.
     */
    override clipElement(context: CanvasRenderingContext2D, elementIndex: number) {
        const clippingPath = new Path2D();
        const [bounds] = this._applyAnimationToIndex(elementIndex);
        const { x, y, width: w, height: h } = bounds;
        clippingPath.rect(x, y, w, h);
        context.clip(clippingPath);
    }

    /** Apply the animation to the bounds at the given index. This will return a new rectangle and fill color. */
    _applyAnimationToIndex(index: number): [BorderBounds, RgbaString] {
        const tempBounds = this._levelBounds[index].clone();

        let start0 = this.start0;
        let end0 = this.end0;
        let start1 = this.start1;
        let end1 = this.end1;
        let fill: RgbaString = int2rgba(this.borderColor, this.borderAlpha);
        const [r, g, b] = string2rgbaArray(fill);
        this.animationConfigurations.forEach((animation) => {
            switch (animation.property) {
                case 'x':
                    {
                        const offset = animation.shouldOffsetPropertyValue ? tempBounds.x : 0;
                        tempBounds.x = animation.computedValues[index] + offset;
                    }
                    break;
                case 'y':
                    {
                        const offset = animation.shouldOffsetPropertyValue ? tempBounds.y : 0;
                        tempBounds.y = animation.computedValues[index] + offset;
                    }
                    break;
                case 'alpha':
                    fill = rgbArray2String([
                        r,
                        g,
                        b,
                        animation.computedValues[index],
                    ]) as RgbaString;
                    break;
                case 'start0':
                    start0 = animation.computedValues[index];
                    break;
                case 'end0':
                    end0 = animation.computedValues[index];
                    break;
                case 'start1':
                    start1 = animation.computedValues[index];
                    break;
                case 'end1':
                    end1 = animation.computedValues[index];
                    break;
            }
        });

        const borderBounds = createBorderBounds(
            tempBounds,
            this.borderThickness,
            start0,
            end0,
            start1,
            end1,
        );
        return [borderBounds, fill];
    }

    override get _numberOfShapes(): number {
        return this._levelBounds.length;
    }

    override _getOffsetForProperty(
        property: AnimatableBoxBorderProperties,
        shouldOffsetPropertyValue: boolean,
    ): number {
        switch (property) {
            case 'x':
            case 'y':
                // offset is handled during draw
                return 0;
            case 'alpha':
            case 'start0':
            case 'end0':
            case 'start1':
            case 'end1':
                if (shouldOffsetPropertyValue) {
                    // During the canvas migration we didn't have any use cases of offsetting these properties for
                    // box borders, so we're not adding support for it here. In general, it probably doesn't
                    // make sense to support offsetting these properties, so it would be nice to clean this up
                    // eventually, but for now just enforce that we can't try to use this combination.
                    throw new Error(`Property ${property} does not support offsetting.`);
                }
                return 0;
            default:
                throw new Error(`Unknown property ${property}`);
        }
    }
}

const createBorderBounds = (
    rectangle: Rectangle,
    borderWidth: number,
    start0: number,
    end0: number,
    start1: number,
    end1: number,
): BorderBounds => {
    // A line is stroked centered on the line vector, so need to remove 1/2 border width from the
    // rect to stroke.
    const xMin = rectangle.x + borderWidth / 2;
    const xMax = rectangle.x + rectangle.width - borderWidth / 2;
    const yMin = rectangle.y + borderWidth / 2;
    const yMax = rectangle.y + rectangle.height - borderWidth / 2;

    const points: [x: number, y: number][] = [
        [xMin, yMin],
        [xMax, yMin],
        [xMax, yMax],
        [xMin, yMax],
    ];

    const segmentLengths = points.map((point, index) => {
        const nextPoint = points[(index + 1) % points.length];
        const x = (point[0] - nextPoint[0]) ** 2;
        const y = (point[1] - nextPoint[1]) ** 2;
        return Math.sqrt(x + y);
    });

    const totalSegmentLength = segmentLengths.reduce((total, num) => {
        return total + num;
    });

    return {
        ...rectangle,
        points,
        segmentLengths,
        totalSegmentLength,
        start0,
        end0,
        start1,
        end1,
    };
};

const positionOnSegment = (borderBounds: BorderBounds, uValue: number) => {
    const position = uValue * borderBounds.totalSegmentLength;
    let absUValue = 0;
    let nAbsUValue = 0;
    for (let i = 0; i < borderBounds.segmentLengths.length; i++) {
        nAbsUValue += borderBounds.segmentLengths[i];
        if (position >= absUValue && position <= nAbsUValue) {
            const ni = i > borderBounds.points.length - 2 ? 0 : i + 1;
            const percentage = (position - absUValue) / borderBounds.segmentLengths[i];
            const x =
                borderBounds.points[i][0] * (1 - percentage) +
                borderBounds.points[ni][0] * percentage;
            const y =
                borderBounds.points[i][1] * (1 - percentage) +
                borderBounds.points[ni][1] * percentage;
            return {
                index: i,
                position: [x, y],
            };
        }
        absUValue += borderBounds.segmentLengths[i];
    }
};

const drawLineSegment = (
    context: CanvasRenderingContext2D,
    borderBounds: BorderBounds,
    index: number,
) => {
    const start = index === 0 ? borderBounds.start0 : borderBounds.start1;
    const end = index === 0 ? borderBounds.end0 : borderBounds.end1;

    if (Math.abs(end - start) < 0.01) {
        return;
    }
    const spos = positionOnSegment(borderBounds, start % 1);
    const epos = positionOnSegment(borderBounds, end % 1);

    if (spos === undefined || epos === undefined) {
        return;
    }

    if (spos.index === epos.index) {
        context.moveTo(spos.position[0], spos.position[1]);
        context.lineTo(epos.position[0], epos.position[1]);
    } else if (spos.index < epos.index) {
        const nSegments = epos.index - spos.index;
        for (let i = 0; i < nSegments + 2; i++) {
            if (i === 0) {
                context.moveTo(spos.position[0], spos.position[1]);
            } else if (i === nSegments + 1) {
                context.lineTo(epos.position[0], epos.position[1]);
            } else {
                context.lineTo(
                    borderBounds.points[spos.index + i][0],
                    borderBounds.points[spos.index + i][1],
                );
            }
        }
    } else {
        const nSegments = borderBounds.segmentLengths.length - spos.index + epos.index;
        for (let i = 0; i < nSegments + 2; i++) {
            if (i === 0) {
                context.moveTo(spos.position[0], spos.position[1]);
            } else if (i === nSegments + 1) {
                context.lineTo(epos.position[0], epos.position[1]);
            } else {
                const index = (spos.index + i) % borderBounds.segmentLengths.length;
                context.lineTo(borderBounds.points[index][0], borderBounds.points[index][1]);
            }
        }
    }
};

const drawBoxBorder = (
    context: CanvasRenderingContext2D,
    borderBounds: BorderBounds,
    color: string,
    borderThickness: number,
) => {
    context.lineWidth = borderThickness;
    context.strokeStyle = color;
    context.beginPath();
    drawLineSegment(context, borderBounds, 0);
    drawLineSegment(context, borderBounds, 1);
    context.stroke();
};
